import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { useProfile } from '@/hooks/useProfile.ts'
import { useWallet } from '@/hooks/useWallet.ts'
import { useActivityLog } from '@/hooks/useActivityLog.ts'
import { ProfileForm } from '@/features/profile/components/ProfileForm.tsx'
import { PlanSelector } from '@/features/subscription/components/PlanSelector.tsx'
import { cn } from '@/utils/cn.ts'

type Step = 'loading' | 'profile' | 'subscription' | 'done'

export function BenefactorOnboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isActive, checkActive, error: subError } = useSubscription()
  const { profile, fetchProfile, saveProfile } = useProfile()
  const { address } = useWallet()
  const { logActivity } = useActivityLog()
  const [step, setStep] = useState<Step>('loading')
  const initialized = useRef(false)

  const initialize = useCallback(async () => {
    if (initialized.current) return
    initialized.current = true

    await checkActive()
    await fetchProfile()
  }, [checkActive, fetchProfile])

  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(() => {
    if (step !== 'loading') return

    if (isActive === true) {
      navigate('/dashboard', { replace: true })
      return
    }

    if (isActive === false || (initialized.current && subError)) {
      const next = profile ? 'subscription' : 'profile'
      const id = setTimeout(() => setStep(next), 0)
      return () => clearTimeout(id)
    }
  }, [isActive, subError, profile, navigate, step])

  const handleProfileSubmit = async (data: {
    email: string
    name?: string
    phone?: string
  }) => {
    const result = await saveProfile(data)
    if (result) {
      await logActivity('profile_updated')
      setStep('subscription')
    }
  }

  const handleSubscribed = async () => {
    await logActivity('subscription_created')
    setStep('done')
    navigate('/dashboard', { replace: true })
  }

  if (step === 'loading') {
    return (
      <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
        <div className="text-fg-muted flex min-h-[300px] items-center justify-center text-[0.9375rem]">
          {t('onboarding.checkingSubscription')}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
        {t('onboarding.benefactorTitle')}
      </h1>
      <p className="text-fg-secondary mb-12">
        {t('onboarding.benefactorSubtitle')}
      </p>

      <div className="mb-12 flex items-center gap-4">
        <div
          className={cn(
            'flex items-center gap-2 text-sm',
            step === 'profile' ? 'text-accent' : 'text-success',
          )}
        >
          <span
            className={cn(
              'border-line flex h-8 w-8 items-center justify-center rounded-full border-2 text-[0.8125rem] font-semibold',
              step === 'profile' && 'border-accent bg-accent-muted text-accent',
              step === 'subscription' &&
                'border-success bg-success-muted text-success',
            )}
          >
            1
          </span>
          <span>{t('onboarding.stepProfile')}</span>
        </div>
        <div className="bg-line h-0.5 w-8" />
        <div
          className={cn(
            'flex items-center gap-2 text-sm',
            step === 'subscription' ? 'text-accent' : 'text-fg-muted',
          )}
        >
          <span
            className={cn(
              'border-line flex h-8 w-8 items-center justify-center rounded-full border-2 text-[0.8125rem] font-semibold',
              step === 'subscription' &&
                'border-accent bg-accent-muted text-accent',
            )}
          >
            2
          </span>
          <span>{t('onboarding.stepSubscribe')}</span>
        </div>
      </div>

      {step === 'profile' && (
        <ProfileForm
          profile={profile}
          walletAddress={address}
          onSubmit={handleProfileSubmit}
          submitLabel={t('onboarding.continue')}
        />
      )}

      {step === 'subscription' && (
        <PlanSelector onSubscribed={handleSubscribed} />
      )}
    </div>
  )
}
